import { useState, useEffect, useRef } from 'react';

import { Country, EFSTAConfCompany, EFSTAConfPos } from './types/pluginTypes';

const emptyConfig = { endpoint: '', country: '-' };

type ConfigValue = typeof emptyConfig;

type Config = {
  [Key in Exclude<Country, '-'>]: ConfigValue;
};

const defaultConfig: Config = {
  AT: emptyConfig,
  DE: emptyConfig,
};

/**
 * Config state that resets every time country is changed.
 * If changed to currently selected country, resets to current config
 * otherwise resets to empty config
 *
 * This ensures that even if multiple countries share the same key for a config
 * the value will never get carried over on switching countries
 */
export function useConfig(initialConfig: EFSTAConfCompany & EFSTAConfPos) {
  const [conf, setConf] = useState(initialConfig);

  const ref = useRef(initialConfig);

  const { country } = conf;
  const { country: initialCountry, endpoint } = ref.current;

  useEffect(() => {
    const newConfig = defaultConfig[country];
    const currentConfig = country === initialCountry ? ref.current : {};
    setConf({
      ...newConfig,
      country,
      ...currentConfig,
      endpoint,
    });
  }, [country, endpoint, initialCountry]);

  return [conf, setConf] as const;
}
